/**
 * Renata Mini PWA — Historial (conversation history) at /renata/historial
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { useEffect, useState } from "react";
import { MessageSquare, Loader2, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { RenataConversation } from "@/lib/renata/types";

export const Route = createFileRoute("/renata/historial")({
  component: RenataMiniHistorial,
  head: () => ({ meta: [{ title: "Renata — Historial" }] }),
});

function RenataMiniHistorial() {
  const tenantId = useWorkspaceTenantId();
  const [conversations, setConversations] = useState<RenataConversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    void fetch("/api/renata/conversations", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    })
      .then((r) => r.json())
      .then((d: { conversations: RenataConversation[] }) => {
        setConversations(d.conversations ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (!tenantId) return null;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="border-b px-4 py-3">
        <h2 className="font-semibold text-sm">Historial de conversaciones</h2>
        <p className="text-xs text-muted-foreground">Tus últimas conversaciones con Renata</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && conversations.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 px-8 text-center">
          <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No hay conversaciones aún.</p>
          <Link
            to="/renata"
            className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white"
          >
            Empezar chat
          </Link>
        </div>
      )}

      <ul className="divide-y">
        {conversations.map((conv) => {
          const date = new Date(conv.updated_at || conv.created_at);
          const label =
            conv.title?.trim() ||
            date.toLocaleString("es-AR", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
          return (
            <li key={conv.id}>
              <Link
                to="/renata"
                search={{ c: conv.id } as never}
                className={cn(
                  "flex items-start gap-3 px-4 py-4 transition-colors hover:bg-muted/50 active:bg-muted",
                )}
              >
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-violet-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{label}</p>
                  {conv.summary && (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{conv.summary}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {date.toLocaleDateString("es-AR", { day: "2-digit", month: "short" })}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
